import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import { AlertTriangle, ShieldAlert, Calendar, User, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * SNAP E&T Expiration Alert Modal
 * 
 * This modal displays hard alerts for SNAP E&T expiration events.
 * It cannot be dismissed by clicking outside, pressing Escape, or using a close button.
 * Users must either Acknowledge (if they are the Assigned User) or Bypass.
 */
const ExpirationAlertModal = ({ participantId, participantName, onComplete }) => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [bypassedAlerts, setBypassedAlerts] = useState(new Set());

  // Fetch alerts when component mounts
  useEffect(() => {
    if (participantId) {
      fetchAlerts();
    }
  }, [participantId]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/participants/${participantId}/expiration-alerts`);
      const activeAlerts = response.data.alerts || [];
      setAlerts(activeAlerts);
      setCurrentAlertIndex(0);
    } catch (error) {
      console.error('Failed to fetch expiration alerts:', error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const currentAlert = alerts[currentAlertIndex];
  
  // Filter out bypassed alerts for this session
  const visibleAlerts = alerts.filter(a => !bypassedAlerts.has(a.id));
  const currentVisibleAlert = visibleAlerts[0]; // Always show first non-bypassed alert

  const handleAcknowledge = async () => {
    if (!currentVisibleAlert) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/participants/${participantId}/expiration-alerts/${currentVisibleAlert.id}/acknowledge`);
      toast.success('Expiration alert acknowledged');
      
      // Remove this alert from the list
      const newAlerts = alerts.filter(a => a.id !== currentVisibleAlert.id);
      setAlerts(newAlerts);
      
      // If no more alerts, complete
      if (newAlerts.filter(a => !bypassedAlerts.has(a.id)).length === 0) {
        onComplete?.();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to acknowledge alert';
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  };

  const handleBypass = async () => {
    if (!currentVisibleAlert) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/participants/${participantId}/expiration-alerts/${currentVisibleAlert.id}/bypass`);
      
      // Add to bypassed set for this session
      setBypassedAlerts(prev => new Set([...prev, currentVisibleAlert.id]));
      
      // If no more visible alerts, complete
      const remaining = alerts.filter(a => !bypassedAlerts.has(a.id) && a.id !== currentVisibleAlert.id);
      if (remaining.length === 0) {
        onComplete?.();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to bypass alert';
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  };

  // Don't show if no visible alerts
  if (loading || !currentVisibleAlert) {
    return null;
  }

  const isAssignedUser = currentVisibleAlert.is_assigned_user;
  const daysUntilExpiration = currentVisibleAlert.days_until_expiration;

  return (
    <Dialog 
      open={true} 
      onOpenChange={() => {}} // Prevent closing
    >
      <DialogContent 
        className="sm:max-w-[600px] border-4 border-red-500 bg-gradient-to-b from-red-50 to-white"
        onPointerDownOutside={(e) => e.preventDefault()} // Prevent closing on outside click
        onEscapeKeyDown={(e) => e.preventDefault()} // Prevent closing on Escape
        onInteractOutside={(e) => e.preventDefault()}
        hideCloseButton={true}
      >
        {/* Pulsing Warning Header */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-red-500 animate-pulse" />
        
        <DialogHeader className="pt-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-red-100 rounded-full animate-pulse">
              <ShieldAlert className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-xl text-red-700 font-bold">
                {currentVisibleAlert.alert_title}
              </DialogTitle>
              <DialogDescription className="text-red-600 font-medium">
                {participantName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Expiration Info */}
          <Alert variant="destructive" className="border-red-300 bg-red-50">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle className="text-lg">
              {daysUntilExpiration === 0 
                ? 'EXPIRES TODAY!' 
                : daysUntilExpiration < 0 
                  ? 'EXPIRED!'
                  : `Expires in ${daysUntilExpiration} day${daysUntilExpiration !== 1 ? 's' : ''}`}
            </AlertTitle>
            <AlertDescription className="mt-2 text-base">
              {currentVisibleAlert.alert_message}
            </AlertDescription>
          </Alert>

          {/* Details */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Expiration Date:</span>
              <span className="font-semibold">{formatDate(currentVisibleAlert.expiration_date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Assigned To:</span>
              <span className="font-semibold">{currentVisibleAlert.assigned_user_name}</span>
            </div>
          </div>

          {/* Assigned User Notice */}
          {isAssignedUser ? (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-blue-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">You are the Assigned User for this case.</span>
              </div>
              <p className="text-sm text-blue-600 mt-1">
                You must acknowledge this alert to confirm you are aware of the upcoming expiration.
              </p>
            </div>
          ) : (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">You are not the Assigned User for this case.</span>
              </div>
              <p className="text-sm text-amber-600 mt-1">
                Only <strong>{currentVisibleAlert.assigned_user_name}</strong> can acknowledge this alert. 
                You may bypass to continue viewing this participant.
              </p>
            </div>
          )}

          {/* Multiple Alerts Notice */}
          {visibleAlerts.length > 1 && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Alert {1} of {visibleAlerts.length} - Additional alerts will appear after this one.</span>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:justify-center">
          {isAssignedUser ? (
            <Button
              onClick={handleAcknowledge}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg font-semibold"
              data-testid="acknowledge-alert-btn"
            >
              {processing ? 'Processing...' : 'Acknowledged'}
            </Button>
          ) : (
            <Button
              onClick={handleBypass}
              disabled={processing}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50 px-8 py-3 text-lg font-semibold"
              data-testid="bypass-alert-btn"
            >
              {processing ? 'Processing...' : 'Bypass'}
            </Button>
          )}
        </DialogFooter>

        {/* Bottom warning bar */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-red-500 animate-pulse" />
      </DialogContent>
    </Dialog>
  );
};

export default ExpirationAlertModal;
